import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { BOT_INVITE_URL, COMMUNITY_INVITE, COMMUNITY_NAME } from "../data/links";

const API_BASE = process.env.REACT_APP_API_BASE;

const FEATURES = [
  {
    title: "Moderation",
    desc: "Automod, warnings, jail, logs, and configurable staff commands.",
  },
  {
    title: "Leveling & shop",
    desc: "XP, essence, quests, pets, garden - plus a role shop for your server.",
  },
  {
    title: "Community tools",
    desc: "Welcome messages, self-role panels, tickets, partnerships, giveaways.",
  },
  {
    title: "Web dashboard",
    desc: "Configure settings in the browser or through slash commands - same data.",
  },
];

const FAQ = [
  {
    q: "How do I add Kami to my server?",
    a: 'Click "Add to Discord" and choose a server where you have Manage Server permission.',
  },
  {
    q: "How do I change bot settings?",
    a: "Log in here with Discord, pick your server, and use the dashboard - or run /settings in Discord.",
  },
  {
    q: "Where can I see all commands?",
    a: (
      <>
        Visit the <Link to="/help">Commands</Link> page, or type{" "}
        <code>/help</code> in your server.
      </>
    ),
  },
  {
    q: "Where can I get support or join the community?",
    a: (
      <>
        Join{" "}
        <a href={COMMUNITY_INVITE} target="_blank" rel="noreferrer">
          {COMMUNITY_NAME}
        </a>{" "}
        on Discord for help, bug reports, and community chat.
      </>
    ),
  },
];

function StatusPill({ label, value, ok }) {
  return (
    <div className="status-pill-item">
      <span className={`status-dot-sm ${ok ? "online" : "offline"}`} />
      <span className="status-pill-label">{label}</span>
      <span className="status-pill-value">{value}</span>
    </div>
  );
}

export default function LandingPage({ user = null }) {
  const [stats, setStats] = useState(null);
  const [openFaq, setOpenFaq] = useState(null);

  useEffect(() => {
    const load = () => {
      fetch(`${API_BASE}/status`)
        .then((res) => res.json())
        .then(setStats)
        .catch(() =>
          setStats({ database: "Offline", bot: "Offline", guilds: 0 }),
        );
    };
    load();
    const interval = setInterval(load, 30000);
    return () => clearInterval(interval);
  }, []);

  const botOk = stats?.bot === "Ready";
  const dbOk = stats?.database === "Connected";

  return (
    <div className="landing-page">
      <div className="landing-inner">
      <section className="landing-hero">
        <div className="landing-hero-copy">
          <p className="landing-eyebrow">☾ Nocturne · Discord community bot</p>
          <h1>Where the quiet gathers.</h1>
          <p className="landing-lead">
            Moderation, leveling, music, welcome flows, role panels, and more -
            all configurable from one gothic-styled control console.
          </p>
          <div className="landing-cta">
            <a
              href={BOT_INVITE_URL}
              target="_blank"
              rel="noreferrer"
              className="btn btn-primary"
            >
              Add to Discord
            </a>
            {user ? (
              <Link to="/servers" className="btn btn-secondary">
                My servers
              </Link>
            ) : (
              <a href={`${API_BASE}/auth/login`} className="btn btn-secondary">
                Log in to manage
              </a>
            )}
            <a
              href={COMMUNITY_INVITE}
              target="_blank"
              rel="noreferrer"
              className="btn btn-secondary"
            >
              Join {COMMUNITY_NAME}
            </a>
          </div>
        </div>

        {stats && (
          <div className="landing-status">
            <StatusPill label="Bot" value={stats.bot} ok={botOk} />
            <StatusPill label="Database" value={stats.database} ok={dbOk} />
            <StatusPill
              label="Servers"
              value={stats.guilds}
              ok={Number(stats.guilds) > 0}
            />
          </div>
        )}
      </section>

      <section className="landing-steps">
        <h2>Getting started</h2>
        <ol className="steps-list">
          <li>
            <strong>Add Kami</strong> to a server you manage.
          </li>
          <li>
            <strong>Log in</strong> with Discord on this site.
          </li>
          <li>
            <strong>Open your server</strong> in the dashboard and configure
            channels, roles, automod, and more.
          </li>
        </ol>
      </section>

      <section className="landing-features">
        <h2>What Kami does</h2>
        <div className="feature-grid">
          {FEATURES.map((f) => (
            <article key={f.title} className="feature-card">
              <h3>{f.title}</h3>
              <p>{f.desc}</p>
            </article>
          ))}
        </div>
      </section>

      <section className="landing-faq">
        <h2>Common questions</h2>
        <div className="faq-list">
          {FAQ.map((item, i) => (
            <div key={item.q} className="faq-row">
              <button
                type="button"
                className="faq-question"
                onClick={() => setOpenFaq(openFaq === i ? null : i)}
                aria-expanded={openFaq === i}
              >
                {item.q}
                <span className="faq-chevron">{openFaq === i ? "−" : "+"}</span>
              </button>
              {openFaq === i && <div className="faq-answer">{item.a}</div>}
            </div>
          ))}
        </div>
      </section>

      <section className="landing-support">
        <h2>Community & support</h2>
        <p>
          Want to hang out, ask questions, or report a bug? Join{" "}
          <strong>{COMMUNITY_NAME}</strong> - the home server for Kami.
        </p>
        <div className="landing-cta">
          <a
            href={COMMUNITY_INVITE}
            target="_blank"
            rel="noreferrer"
            className="btn btn-primary"
          >
            Join {COMMUNITY_NAME}
          </a>
          <a href="mailto:thekamigrove@gmail.com" className="btn btn-secondary">
            Email us
          </a>
        </div>
      </section>
      </div>
    </div>
  );
}
